import React, { useState, useEffect } from "react";
import Wordmark from "../components/Wordmark";
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { db, app, superAdminStatsFn, ClubStats } from "../firebase";
import { getStorage } from "firebase/storage";
import { Club } from "../types";

const storage = getStorage(app);

async function ensureAuth(): Promise<void> {
  const auth = getAuth(app);
  if (auth.currentUser) return;
  await signInAnonymously(auth);
  await new Promise<void>((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => { if (u) { unsub(); resolve(); } });
  });
}

const C = {
  card: "var(--sl-card)",
  navy: "var(--sl-primary)", gold: "var(--sl-accent)", text: "var(--sl-text)",
  muted: "var(--sl-muted)", bg: "var(--sl-bg)", white: "#ffffff",
  border: "var(--sl-border)", error: "var(--sl-error)", soft: "var(--sl-soft)",
  green: "#22c55e",
};
const F = { heading: "'Montserrat', sans-serif", body: "'Inter', sans-serif" };

const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 12,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};
const btn: React.CSSProperties = {
  padding: "10px 16px", borderRadius: 10, border: "none",
  cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: F.body,
};
const inp: React.CSSProperties = {
  width: "100%", padding: "11px 13px", boxSizing: "border-box",
  background: C.bg, border: `1.5px solid ${C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text,
  fontFamily: F.body, outline: "none", marginBottom: 10,
};

// "12 pv sitten" — kertoo yhdellä silmäyksellä onko klubi elossa.
function sinceLabel(iso: string | null): string {
  if (!iso) return "ei tapahtumia";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "ei tapahtumia";
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days < 0) return `${-days} pv kuluttua`;
  if (days === 0) return "tänään";
  if (days === 1) return "eilen";
  if (days < 60) return `${days} pv sitten`;
  return `${Math.floor(days / 30)} kk sitten`;
}

// Firestore kaataa kirjoituksen ilman selitystä, jos tokenista puuttuu
// superAdmin-claim. Se näkyy käyttäjälle muuten pelkkänä jumina, joten
// yleisin syy sanotaan ääneen: istunto on vanhentunut ja PIN pitää antaa
// uudelleen. Muut virheet näytetään sellaisenaan — arvaus olisi huonompi
// kuin Firebasen oma teksti.
function virhe(e: unknown): string {
  const code = (e as { code?: string })?.code || "";
  if (code === "permission-denied" || code === "unauthenticated") {
    return "Ei oikeutta. Super admin -istunto on todennäköisesti vanhentunut — "
      + "palaa takaisin ja kirjaudu uudelleen PIN-koodilla.";
  }
  const msg = (e as { message?: string })?.message;
  return msg ? `Virhe: ${msg}` : "Tuntematon virhe.";
}

function genCode(name: string): string {
  const base = name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  const num  = Math.floor(1000 + Math.random() * 9000);
  return `${base}-${num}`;
}

const DEFAULT_COLORS = [
  "var(--sl-primary)", "#1d4ed8", "#0f766e", "#15803d",
  "#b45309", "#9333ea", "#be123c", "#1e293b",
];

interface Props { onLogout: () => void; }

export default function SuperAdminView({ onLogout }: Props) {
  const [clubs, setClubs]         = useState<Club[]>([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [newName, setNewName]     = useState("");
  const [newPin, setNewPin]       = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData]   = useState<Partial<Club>>({});
  const [saving, setSaving]       = useState(false);
  const [stats, setStats]         = useState<Record<string, ClubStats> | null>(null);
  const [totals, setTotals]       = useState<{
    clubs: number; players: number; events: number;
    games: number; played: number; signups: number;
  } | null>(null);
  const [statsError, setStatsError] = useState(false);
  const [clubsError, setClubsError]  = useState("");
  const [createError, setCreateError] = useState("");
  const [createBusy, setCreateBusy]  = useState(false);

  async function loadClubs() {
    setClubsError("");
    try {
      await ensureAuth();
      const snap = await getDocs(collection(db, "clubs"));
    // adminPin asuu salaisessa alikokoelmassa, jota vain super admin saa
    // lukea. Se haetaan erikseen, jotta klubidokumentin voi pitää
    // luettavana ilman että PIN vuotaa mukana.
      const withPins = await Promise.all(snap.docs.map(async (d) => {
        const club = { id: d.id, ...d.data() } as Club;
        try {
          const secret = await getDoc(doc(db, "clubs", d.id, "private", "auth"));
          if (secret.exists()) club.adminPin = secret.data().adminPin;
        } catch { /* ei lukuoikeutta — jätetään näyttämättä */ }
        return club;
      }));
      setClubs(withPins);
    } catch (e) {
      // Ilman tätä lista jäi ikuisesti tilaan "Ladataan...": setLoading(false)
      // oli vain onnistumispolulla.
      setClubsError(virhe(e));
    } finally {
      setLoading(false);
    }
  }

  // Luvut tulevat palvelimelta: klubien alikokoelmat eivät ole
  // asiakkaan luettavissa, eikä niitä kannattaisi hakea dokumentti
  // kerrallaan vaikka olisivatkin. Klubilista ei odota tätä.
  async function loadStats() {
    setStatsError(false);
    try {
      const res = await superAdminStatsFn();
      const byId: Record<string, ClubStats> = {};
      res.data.clubs.forEach((c) => { byId[c.clubId] = c; });
      setStats(byId);
      setTotals(res.data.totals);
    } catch {
      setStatsError(true);
    }
  }

  useEffect(() => { loadClubs(); loadStats(); }, []);

  async function createClub() {
    if (!newName.trim() || !newPin.trim()) return;
    setCreateError(""); setCreateBusy(true);
    try {
      await ensureAuth();
      const code = genCode(newName);
      const ref2 = doc(collection(db, "clubs"));
      await setDoc(ref2, {
        name: newName.trim(), clubCode: code,
        createdAt: new Date().toISOString(),
      });
      // PIN erilliseen alikokoelmaan, jotta se ei kulje klubidokumentin
      // mukana jokaiselle joka klubin lukee.
      await setDoc(doc(db, "clubs", ref2.id, "private", "auth"), { adminPin: newPin.trim() });
      setCreating(false); setNewName(""); setNewPin("");
      loadClubs();
    } catch (e) {
      // Lomake jää auki syötteineen, jotta yrittämistä ei tarvitse
      // aloittaa alusta. Ilman tätä haaraa nappi ei tehnyt yhtään mitään.
      setCreateError(virhe(e));
    } finally {
      setCreateBusy(false);
    }
  }

  async function deleteClub(c: Club) {
    if (!window.confirm(`Poistetaanko klubi "${c.name}"? Tätä ei voi peruuttaa.`)) return;
    try {
      await ensureAuth();
      await deleteDoc(doc(db, "clubs", c.id));
      loadClubs();
    } catch (e) {
      setClubsError(virhe(e));
    }
  }

  async function uploadLogo(c: Club, file: File) {
    setUploading(c.id);
    try {
      await ensureAuth();
      const storageRef = ref(storage, `club-logos/${c.clubCode}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "clubs", c.id), { logoUrl: url });
      loadClubs();
    } catch (e) { alert("Logo-lataus epäonnistui. Tarkista Firebase Storage -säännöt."); }
    setUploading(null);
  }

  function startEdit(c: Club) {
    setEditingId(c.id);
    setEditData({
      address: c.address || "",
      contactPerson: c.contactPerson || "",
      contactPhone: c.contactPhone || "",
      primaryColor: c.primaryColor || C.navy,
      secondaryColor: c.secondaryColor || C.gold,
    });
  }

  async function saveEdit(c: Club) {
    setSaving(true);
    try {
      await ensureAuth();
      await updateDoc(doc(db, "clubs", c.id), {
        address: editData.address || "",
        contactPerson: editData.contactPerson || "",
        contactPhone: editData.contactPhone || "",
        primaryColor: editData.primaryColor || C.navy,
        secondaryColor: editData.secondaryColor || C.gold,
      });
      setEditingId(null);
      loadClubs();
    } catch { alert("Tallennus epäonnistui."); }
    setSaving(false);
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", maxWidth: 520, margin: "0 auto", fontFamily: F.body }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, #0d1f3c, var(--sl-primary))`,
        padding: "20px 20px", display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: C.white, fontFamily: F.heading, letterSpacing: 1 }}>
            <Wordmark />
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>Super Admin</div>
        </div>
        <button onClick={onLogout} style={{ ...btn, background: "rgba(255,255,255,0.1)", color: C.white, fontSize: 12 }}>
          Takaisin
        </button>
      </div>

      <div style={{ padding: "16px 16px 80px" }}>
        {/* Käyttö yhteensä */}
        {totals && (
          <div style={{
            ...card, padding: "14px 16px", marginBottom: 16,
            background: `linear-gradient(135deg, ${C.navy}, #2a5c8f)`, border: "none",
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.55)",
              letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 10,
            }}>
              Käyttö yhteensä
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {([
                ["Klubia", totals.clubs],
                ["Pelaajaa", totals.players],
                ["Tapahtumaa", totals.events],
                ["Pelattu", totals.played],
              ] as [string, number][]).map(([label, n]) => (
                <div key={label} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: C.white, fontFamily: F.heading }}>{n}</div>
                  <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.45)", marginTop: 10, textAlign: "center" }}>
              {totals.signups} ilmoittautumista · {totals.games} ottelua luotu,
              {" "}{totals.games - totals.played} ilman tulosta
            </div>
          </div>
        )}
        {statsError && (
          <div style={{ ...card, padding: "12px 14px", marginBottom: 16, background: "#fffbeb", borderColor: "#fde68a" }}>
            <div style={{ fontSize: 12, color: "#92400e" }}>
              Käyttölukuja ei saatu haettua.{" "}
              <button onClick={loadStats} style={{
                ...btn, background: "transparent", color: "#92400e", padding: 0,
                fontSize: 12, textDecoration: "underline",
              }}>
                Yritä uudelleen
              </button>
            </div>
          </div>
        )}

        {clubsError && (
          <div style={{ ...card, padding: "12px 14px", marginBottom: 16, background: "#fef2f2", borderColor: "#fecaca" }}>
            <div style={{ fontSize: 12, color: "#991b1b", marginBottom: 6 }}>{clubsError}</div>
            <button onClick={loadClubs} style={{
              ...btn, background: "transparent", color: "#991b1b", padding: 0,
              fontSize: 12, textDecoration: "underline",
            }}>
              Yritä uudelleen
            </button>
          </div>
        )}

        {/* Luo klubi */}
        {!creating ? (
          <button onClick={() => setCreating(true)} style={{
            ...btn, width: "100%", padding: "14px 0", marginBottom: 16,
            background: `linear-gradient(135deg, ${C.navy}, #2a5c8f)`,
            color: C.white, fontSize: 15,
          }}>
            + Luo uusi klubi
          </button>
        ) : (
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 14, fontFamily: F.heading }}>
              UUSI KLUBI
            </div>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Klubin nimi" style={inp} autoFocus />
            <input value={newPin}  onChange={e => setNewPin(e.target.value)}  placeholder="Admin PIN (esim. 1234)" type="password" style={inp} />
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
              Klubikoodi generoidaan automaattisesti nimestä.
            </div>
            {createError && (
              <div style={{ fontSize: 12, color: C.error, marginBottom: 12, lineHeight: 1.45 }}>
                {createError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={createClub} style={{
                ...btn, flex: 1, background: C.navy, color: C.white,
                opacity: createBusy ? 0.6 : 1,
              }}
                disabled={createBusy || !newName.trim() || !newPin.trim()}>
                {createBusy ? "Luodaan…" : "Luo klubi"}
              </button>
              <button onClick={() => setCreating(false)} style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
                Peruuta
              </button>
            </div>
          </div>
        )}

        {/* Klubilista */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
          {clubs.length} klubia
        </div>

        {loading && <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Ladataan...</div>}

        {clubs.map(c => {
          const primary   = c.primaryColor   || C.navy;
          const secondary = c.secondaryColor || C.gold;
          const isEditing = editingId === c.id;

          return (
            <div key={c.id} style={{ ...card, borderTop: `4px solid ${primary}` }}>
              {/* Klubin otsikkorivi */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* Logo / Väri-avatar */}
                <div style={{
                  width: 48, height: 48, borderRadius: 12, overflow: "hidden", flexShrink: 0,
                  background: c.logoUrl ? C.soft : primary,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: `2px solid ${secondary}`,
                }}>
                  {c.logoUrl ? (
                    <img src={c.logoUrl} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <span style={{ fontSize: 20, fontWeight: 900, color: secondary }}>{c.name[0]}</span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 700, color: primary }}>{c.clubCode}</span>
                    {" · PIN: "}{c.adminPin}
                  </div>
                  {(c.address || c.contactPerson) && (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.4 }}>
                      {c.address && <span>📍 {c.address}</span>}
                      {c.address && c.contactPerson && <span> · </span>}
                      {c.contactPerson && <span>👤 {c.contactPerson}</span>}
                      {c.contactPhone && <span> · 📞 {c.contactPhone}</span>}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {/* Logo-upload */}
                  <label style={{ ...btn, background: C.soft, color: C.muted, cursor: "pointer", padding: "8px 10px", fontSize: 16 }}>
                    {uploading === c.id ? "⏳" : "🖼"}
                    <input type="file" accept="image/*" style={{ display: "none" }}
                      onChange={e => { if (e.target.files?.[0]) uploadLogo(c, e.target.files[0]); }} />
                  </label>

                  {/* Muokkaa */}
                  <button onClick={() => isEditing ? setEditingId(null) : startEdit(c)}
                    style={{ ...btn, background: isEditing ? C.soft : `${primary}18`, color: isEditing ? C.muted : primary, padding: "8px 10px", fontSize: 15 }}>
                    {isEditing ? "✕" : "✏️"}
                  </button>

                  {/* Poista */}
                  <button onClick={() => deleteClub(c)} style={{ ...btn, background: "#fef2f2", color: C.error, padding: "8px 10px", fontSize: 16 }}>
                    🗑
                  </button>
                </div>
              </div>

              {/* Käyttöluvut */}
              {stats?.[c.id] && (
                <div style={{
                  display: "flex", gap: 6, marginTop: 12, paddingTop: 12,
                  borderTop: `1px solid ${C.border}`, alignItems: "center",
                }}>
                  {([
                    ["pelaajaa", stats[c.id].players],
                    ["tapahtumaa", stats[c.id].events],
                    ["pelattu", stats[c.id].played],
                  ] as [string, number][]).map(([label, n]) => (
                    <div key={label} style={{ flex: 1, textAlign: "center" }}>
                      <div style={{
                        fontSize: 16, fontWeight: 800, fontFamily: F.heading,
                        color: n > 0 ? C.text : C.muted,
                      }}>
                        {n}
                      </div>
                      <div style={{ fontSize: 9.5, color: C.muted, marginTop: 1 }}>{label}</div>
                    </div>
                  ))}
                  <div style={{ flex: 1.3, textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>
                      {sinceLabel(stats[c.id].lastEventAt)}
                    </div>
                    <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>viimeisin</div>
                  </div>
                </div>
              )}

              {/* Muokkauslomake */}
              {isEditing && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 12 }}>
                    Yhteystiedot
                  </div>
                  <input
                    value={editData.address || ""}
                    onChange={e => setEditData(d => ({ ...d, address: e.target.value }))}
                    placeholder="Osoite (esim. Kenttipolku 1, Helsinki)"
                    style={inp}
                  />
                  <input
                    value={editData.contactPerson || ""}
                    onChange={e => setEditData(d => ({ ...d, contactPerson: e.target.value }))}
                    placeholder="Yhteyshenkilö"
                    style={inp}
                  />
                  <input
                    value={editData.contactPhone || ""}
                    onChange={e => setEditData(d => ({ ...d, contactPhone: e.target.value }))}
                    placeholder="Puhelinnumero"
                    style={{ ...inp, marginBottom: 16 }}
                  />

                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
                    Klubin värit
                  </div>

                  {/* Pääväri */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: C.text, fontWeight: 600, marginBottom: 6 }}>Pääväri</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {DEFAULT_COLORS.map(col => (
                        <button key={col} onClick={() => setEditData(d => ({ ...d, primaryColor: col }))}
                          style={{
                            width: 32, height: 32, borderRadius: 8, background: col, border: "none", cursor: "pointer",
                            outline: editData.primaryColor === col ? `3px solid ${C.gold}` : "none",
                            outlineOffset: 2,
                          }}
                        />
                      ))}
                      <input
                        type="color"
                        value={editData.primaryColor || C.navy}
                        onChange={e => setEditData(d => ({ ...d, primaryColor: e.target.value }))}
                        style={{ width: 32, height: 32, borderRadius: 8, border: "none", cursor: "pointer", padding: 0 }}
                        title="Oma väri"
                      />
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: editData.primaryColor || C.navy, border: `1px solid ${C.border}` }} />
                    </div>
                  </div>

                  {/* Toissijainen väri */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: C.text, fontWeight: 600, marginBottom: 6 }}>Korostusväri</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {["var(--sl-accent)", "#fbbf24", "#4ade80", "#38bdf8", "#a78bfa", "#f472b6", "#ffffff", "#e2e8f0"].map(col => (
                        <button key={col} onClick={() => setEditData(d => ({ ...d, secondaryColor: col }))}
                          style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: col, border: `1px solid ${C.border}`, cursor: "pointer",
                            outline: editData.secondaryColor === col ? `3px solid ${C.navy}` : "none",
                            outlineOffset: 2,
                          }}
                        />
                      ))}
                      <input
                        type="color"
                        value={editData.secondaryColor || C.gold}
                        onChange={e => setEditData(d => ({ ...d, secondaryColor: e.target.value }))}
                        style={{ width: 32, height: 32, borderRadius: 8, border: "none", cursor: "pointer", padding: 0 }}
                        title="Oma väri"
                      />
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: editData.secondaryColor || C.gold, border: `1px solid ${C.border}` }} />
                    </div>
                  </div>

                  {/* Esikatselu */}
                  <div style={{
                    borderRadius: 10, padding: "10px 14px", marginBottom: 14,
                    background: editData.primaryColor || C.navy,
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: editData.secondaryColor || C.gold,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 900, color: editData.primaryColor || C.navy,
                    }}>
                      {c.name[0]}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>{c.name}</div>
                      <div style={{ fontSize: 10, color: editData.secondaryColor || C.gold, marginTop: 1 }}>{c.clubCode}</div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => saveEdit(c)} disabled={saving}
                      style={{ ...btn, flex: 1, background: C.navy, color: C.white }}>
                      {saving ? "Tallennetaan..." : "Tallenna"}
                    </button>
                    <button onClick={() => setEditingId(null)}
                      style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
                      Peruuta
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
